import { MagnifyingGlass } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import c from './CommandPalette.module.css';
import { downloadText } from '../io/download';
import { projectFile, slug } from '../io/project-file';
import { addArea, addEquipment, addSource, addSubstance, addUnderlay, deleteSelection, duplicateSelection } from '../state/actions';
import { useStore, type Selection } from '../state/store';

interface Item {
  id: string;
  label: string;
  hint?: string;
  group: 'Actions' | 'Sources' | 'Equipment' | 'Areas' | 'Substances';
  run: () => void;
}

function pickPlan() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/png,image/jpeg,image/webp';
  input.onchange = () => { const f = input.files?.[0]; if (f) void addUnderlay(f); };
  input.click();
}

/** Ctrl/⌘ K: every action and every object by tag or description. */
export function CommandPalette() {
  const open = useStore((s) => s.paletteOpen);
  const doc = useStore((s) => s.doc);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const close = () => useStore.getState().setPalette(false);

  useEffect(() => { if (open) { setQ(''); setActive(0); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);

  const items = useMemo<Item[]>(() => {
    if (!doc) return [];
    const s = useStore.getState();
    const go = (sel: NonNullable<Selection>) => () => { s.select(sel); s.setScreen('workspace'); s.requestFocus(); };
    const view = (mode: 'persp' | 'plan' | 'elev') => () => { s.setScreen('workspace'); s.setView({ mode }); };
    const actions: Item[] = [
      { id: 'add-source', label: 'Add a source of release', hint: 'S', group: 'Actions', run: addSource },
      { id: 'add-eq', label: 'Add equipment', hint: 'E', group: 'Actions', run: addEquipment },
      { id: 'add-room', label: 'Add a room (indoor area)', group: 'Actions', run: () => addArea('indoor') },
      { id: 'add-out', label: 'Add an outdoor area', group: 'Actions', run: () => addArea('outdoor') },
      { id: 'add-sub', label: 'Add a substance', group: 'Actions', run: addSubstance },
      { id: 'add-plan', label: 'Add a site plan image', group: 'Actions', run: pickPlan },
      { id: 'v-3d', label: '3D view', hint: 'V', group: 'Actions', run: view('persp') },
      { id: 'v-plan', label: 'Plan view', hint: 'P', group: 'Actions', run: view('plan') },
      { id: 'v-elev', label: 'Elevation view', hint: 'L', group: 'Actions', run: view('elev') },
      { id: 'fit', label: 'Fit the view to the selection', hint: 'F', group: 'Actions', run: () => s.requestFocus() },
      { id: 'tags', label: 'Show or hide tags', group: 'Actions', run: () => s.setView({ labels: !s.view.labels }) },
      { id: 'dossier', label: 'Open the dossier', group: 'Actions', run: () => s.setScreen('dossier') },
      { id: 'checks', label: 'Show checks', group: 'Actions', run: () => s.setView({ dockTab: 'checks', dockOpen: true }) },
      { id: 'summation', label: 'Show the summation of releases', group: 'Actions', run: () => s.setView({ dockTab: 'summation', dockOpen: true }) },
      { id: 'undo', label: 'Undo', hint: 'Ctrl Z', group: 'Actions', run: s.undo },
      { id: 'redo', label: 'Redo', hint: 'Ctrl Shift Z', group: 'Actions', run: s.redo },
      { id: 'dup', label: 'Duplicate the selection', hint: 'Ctrl D', group: 'Actions', run: duplicateSelection },
      { id: 'del', label: 'Delete the selection', hint: 'Del', group: 'Actions', run: deleteSelection },
      { id: 'export', label: 'Download the project file', group: 'Actions', run: () => downloadText(`${slug(doc.meta.name)}.extent.json`, JSON.stringify(projectFile(doc), null, 2), 'application/json') },
      { id: 'projects', label: 'All projects', group: 'Actions', run: s.closeProject },
    ];
    return [
      ...actions,
      ...doc.sources.map((x): Item => ({ id: `s:${x.id}`, label: `${x.tag} — ${x.description}`, group: 'Sources', run: go({ kind: 'source', id: x.id }) })),
      ...doc.equipment.map((x): Item => ({ id: `e:${x.id}`, label: `${x.tag} — ${x.description}`, group: 'Equipment', run: go({ kind: 'equipment', id: x.id }) })),
      ...doc.areas.map((x): Item => ({ id: `a:${x.id}`, label: x.name, group: 'Areas', run: go({ kind: 'area', id: x.id }) })),
      ...doc.substances.map((x): Item => ({ id: `u:${x.id}`, label: x.name, group: 'Substances', run: () => { s.select({ kind: 'substance', id: x.id }); s.setScreen('workspace'); } })),
    ];
  }, [doc]);

  const shown = useMemo(() => {
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    const hits = terms.length ? items.filter((i) => terms.every((t) => `${i.label} ${i.group}`.toLowerCase().includes(t))) : items.filter((i) => i.group === 'Actions');
    return hits.slice(0, 40);
  }, [items, q]);

  if (!open || !doc) return null;
  const run = (i: Item | undefined) => { if (!i) return; close(); i.run(); };
  return (
    <div className={c.backdrop} onMouseDown={close}>
      <div className={c.panel} role="dialog" aria-label="Command palette" onMouseDown={(e) => e.stopPropagation()}>
        <label className={c.search}>
          <MagnifyingGlass size={16} aria-hidden="true" />
          <input
            ref={inputRef} value={q} placeholder="Type a command, a tag or a description"
            aria-label="Search commands and objects" aria-controls="palette-list" aria-activedescendant={shown[active] ? `pal-${shown[active].id}` : undefined}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); close(); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(shown.length - 1, a + 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
              else if (e.key === 'Enter') { e.preventDefault(); run(shown[active]); }
            }}
          />
        </label>
        <ul id="palette-list" className={c.list} role="listbox">
          {shown.map((i, idx) => (
            <li key={i.id} id={`pal-${i.id}`} role="option" aria-selected={idx === active} className={c.item} onMouseEnter={() => setActive(idx)} onClick={() => run(i)}>
              <span className={c.group}>{i.group === 'Actions' ? '' : i.group.replace(/s$/, '')}</span>
              <span className={c.label}>{i.label}</span>
              {i.hint && <kbd className={c.kbd}>{i.hint}</kbd>}
            </li>
          ))}
          {!shown.length && <li className={c.none}>Nothing matches “{q}”.</li>}
        </ul>
      </div>
    </div>
  );
}
